import { defineStore } from 'pinia';
import { ref, computed } from 'vue';

interface User {
  sub: string;
  email?: string;
  name?: string;
  preferredUsername?: string;
  roles: string[];
  groups: string[];
}

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null);
  const loading = ref(true);
  const error = ref<string | null>(null);
  const initialized = ref(false);

  const isAuthenticated = computed(() => !!user.value);
  const isAdmin = computed(() => user.value?.roles.includes('admin') ?? false);
  const isDeveloper = computed(() => user.value?.roles.includes('developer') ?? false);
  const canWrite = computed(() => isAdmin.value || isDeveloper.value || organizationSlugs.value.length > 0);

  // For backwards compatibility with code expecting a token
  const token = computed(() => isAuthenticated.value ? 'session' : null);

  // Extract organization slugs from groups (e.g., /organizations/enspirit/developers -> enspirit)
  const organizationSlugs = computed(() => {
    if (!user.value?.groups) return [];
    const slugs = new Set<string>();
    const pattern = /^\/organizations\/([a-z0-9-]+)\/(admins|developers)$/;
    for (const group of user.value.groups) {
      const match = group.match(pattern);
      if (match) {
        slugs.add(match[1]);
      }
    }
    return Array.from(slugs);
  });

  async function init(): Promise<boolean> {
    loading.value = true;
    error.value = null;

    try {
      const response = await fetch('/api/auth/me', {
        credentials: 'include',
      });

      initialized.value = true;

      if (response.ok) {
        user.value = await response.json();
        return true;
      } else if (response.status === 401) {
        user.value = null;
        return false;
      } else {
        throw new Error(`Unexpected response: ${response.status}`);
      }
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Failed to check authentication';
      initialized.value = true;
      return false;
    } finally {
      loading.value = false;
    }
  }

  function login(returnTo?: string) {
    const params = returnTo ? `?returnTo=${encodeURIComponent(returnTo)}` : '';
    window.location.href = `/api/auth/login${params}`;
  }

  function logout() {
    user.value = null;
    window.location.href = '/api/auth/logout';
  }

  return {
    token,
    user,
    loading,
    error,
    initialized,
    isAuthenticated,
    isAdmin,
    isDeveloper,
    canWrite,
    organizationSlugs,
    init,
    login,
    logout,
  };
});
